/**
 * Tracking utility for capturing referrer and UTM parameters
 * Stores tracking data in localStorage for persistence across sessions
 */

const TRACKING_STORAGE_KEY = "dm2buy_tracking_data";

/**
 * Extract UTM parameters from URL
 * @param {string} url - URL to extract parameters from (defaults to current URL)
 * @returns {Object} Object containing UTM parameters
 */
const extractUTMParameters = (url = window.location.href) => {
  const urlObj = new URL(url);
  const utmParams = {};

  // Standard UTM parameters
  const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

  utmKeys.forEach((key) => {
    const value = urlObj.searchParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  return utmParams;
};

/**
 * Get referrer information
 * @returns {Object} Object containing referrer data
 */
const getReferrerData = () => {
  const referrer = document.referrer;
  if (!referrer) return {};

  try {
    const referrerUrl = new URL(referrer);
    return {
      referrer_domain: referrerUrl.hostname,
      referrer_url: referrer,
      referrer_path: referrerUrl.pathname,
    };
  } catch (error) {
    // If referrer is not a valid URL, just store it as is
    return {
      referrer_url: referrer,
    };
  }
};

/**
 * Capture and store tracking data
 * This should be called when the page loads
 */
const captureTrackingData = () => {
  if (typeof window === "undefined") return;

  const utmParams = extractUTMParameters();
  const referrerData = getReferrerData();

  // Only capture if we have UTM parameters or referrer
  if (Object.keys(utmParams).length === 0 && Object.keys(referrerData).length === 0) {
    return;
  }

  const trackingData = {
    ...utmParams,
    ...referrerData,
    captured_at: new Date().toISOString(),
    landing_page: window.location.href,
  };

  // Store in localStorage
  try {
    localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(trackingData));
    console.log("Tracking data captured:", trackingData);
  } catch (error) {
    console.error("Failed to store tracking data:", error);
  }

  return trackingData;
};

/**
 * Get stored tracking data
 * @returns {Object|null} Stored tracking data or null if not found
 */
const getStoredTrackingData = () => {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(TRACKING_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Failed to retrieve tracking data:", error);
    return null;
  }
};

/**
 * Clear stored tracking data
 */
const clearTrackingData = () => {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(TRACKING_STORAGE_KEY);
    console.log("Tracking data cleared");
  } catch (error) {
    console.error("Failed to clear tracking data:", error);
  }
};

/**
 * Get tracking data for order payload
 * Returns the stored tracking data as a single object for order submission
 * @returns {Object} Tracking data object for order payload
 */
const getTrackingDataForOrder = () => {
  const trackingData = getStoredTrackingData();
  if (!trackingData) return {};

  // Return the tracking data as a single object
  return {
    tracking_data: trackingData,
  };
};

/**
 * Initialize tracking on page load
 * This should be called in _app.js or similar
 */
const initializeTracking = () => {
  if (typeof window === "undefined") return;

  // Capture tracking data on page load
  captureTrackingData();
};

export {
  captureTrackingData,
  getStoredTrackingData,
  clearTrackingData,
  getTrackingDataForOrder,
  initializeTracking,
  extractUTMParameters,
  getReferrerData,
};
